/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { Render } from '@atlassian/forge-ui-types';
import { elementToForgeDoc } from '../../../utils/elementToForgeDoc';

import React from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import PlatformSectionMessage from '@atlaskit/section-message';
import { useIsMacro } from '../../utils/useIsModule';
import { css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';

type PlatformSectionMessageProps = React.ComponentProps<typeof PlatformSectionMessage>;

export type SectionMessageProps = Pick<
	PlatformSectionMessageProps,
	'children' | 'appearance' | 'title' | 'actions' | 'testId'
>;

const renderActions = (render: Render, actions: React.ReactElement | React.ReactElement[]) =>
	Array.isArray(actions)
		? actions.map((action: React.ReactElement) => render(elementToForgeDoc(action)))
		: [render(elementToForgeDoc(actions))];

const macroStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'section > div > div > h2': {
		//`ProseMirror` styles is overriding the title style with a much higher specificity as it contains multiple class selectors holding more weight.
		// We're forced the use `!important` here since we can't wrap title in it's own class name - the prop only allows for a string.
		// Wrapping the component in more classes to increase specificity pollutes the DOM.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		font: `${token('font.heading.small')} !important`,
	},
});

/**
 * A section message is used to alert users to a particular section of the screen.
 */
export const SectionMessage = (props: Parameters<RenderFn>[0]): JSX.Element => {
	const { appearance, title, actions, testId } = props.forgeDoc.props as SectionMessageProps;

	const isMacro = useIsMacro();

	if (isMacro) {
		return (
			<span css={macroStyle}>
				<PlatformSectionMessage
					children={props.forgeDoc.children.map((c) => (
						<React.Fragment key={c.key}>{props.render(c)}</React.Fragment>
					))}
					appearance={appearance}
					title={title}
					// pre-render actions before inserting as props
					actions={
						actions ? (renderActions(props.render, actions) as React.ReactElement[]) : undefined
					}
					testId={testId}
				/>
			</span>
		);
	}

	return (
		<PlatformSectionMessage
			children={props.forgeDoc.children.map((c) => (
				<React.Fragment key={c.key}>{props.render(c)}</React.Fragment>
			))}
			appearance={appearance}
			title={title}
			// pre-render actions before inserting as props
			actions={actions ? (renderActions(props.render, actions) as React.ReactElement[]) : undefined}
			testId={testId}
		/>
	);
};
